import React from 'react';

import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectMembersCount,
    selectTicketVisibility,
} from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketVisibilityForDisplay } from 'flow/types/Tickets';

const SUMMARY_KEYS: Record<TicketVisibilityForDisplay, string> = {
    private: 'tickets.access.summary_private',
    restricted: 'tickets.access.summary_restricted',
    public: 'tickets.access.summary_public',
    unknown: 'tickets.access.summary_unknown',
};

interface Props {
    ticketKey: TicketKey;
}

/**
 * States the access in one line and opens the dialog that changes it. Built as a
 * labelled field rather than a navigation row, so it reads as one of the ticket's
 * settings alongside Assignee, Status and Subject.
 */
const TicketAccessField = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const membersCount = useAppSelector((state) =>
        selectMembersCount(state, ticketKey),
    );
    const visibility = useAppSelector((state) =>
        selectTicketVisibility(state, ticketKey),
    );

    return (
        <div>
            <div className="mb-1.5 text-xs font-bold">
                {i18next.t('tickets.access.title')}
            </div>
            <button
                type="button"
                onClick={() => openModal('ticketAccess', { ticketKey })}
                className="hover:darken-10 flex h-10 w-full items-center justify-between gap-2 rounded-sm bg-gray-200 px-3 text-sm text-gray-800"
            >
                <span className="truncate text-start">
                    {i18next.t(SUMMARY_KEYS[visibility], {
                        count: membersCount ?? 0,
                    })}
                </span>
                <Icon
                    iconKey="tune"
                    className="shrink-0 text-gray-600"
                    size="1.25rem"
                />
            </button>
        </div>
    );
};

export default React.memo(TicketAccessField);
